import React, { FC } from 'react';
import Slider from 'react-slick';
import styled from 'styled-components';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import ChevronArrowSVG from '../../assets/icons/chevron-arrow.svg';
import { MOBILE_BREAKPOINT, colors } from '../constants';

interface CarouselProps {
  cards: Array<{
    id: string;
    image: string;
    label: string;
    shieldImage?: boolean;
  }>;
}

interface CarouselCardProps {
  id: string;
  image: string;
  label: string;
  shieldImage: boolean;
}

interface StyledProps {
  shieldImage: boolean;
}

const Container = styled.section`
  position: relative;
  height: 8.75rem;
  width: 100%;
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: linear-gradient(
    180deg,
    rgba(255, 235, 217, 0.24) 0%,
    rgba(255, 235, 217, 0.79) 100%
  );
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 2.5rem 0 0;
  }
`;
Container.displayName = 'Container';

const SliderWindow = styled.div`
  width: 100%;
`;
SliderWindow.displayName = 'SliderWindow';

const SliderCard = styled.div`
  position: relative;
  padding: 2.25rem 2.5rem 2.5rem;
  display: flex;
  flex-direction: row;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 1.5rem;
  }
`;
SliderCard.displayName = 'SliderCard';

const SliderImageContainer = styled.div<StyledProps>`
  height: 3.8125rem;
  width: 3.8125rem;
  border-radius: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 0.375rem 1.5rem rgba(0, 0, 0, 0.15);
  background: radial-gradient(100% 100% at 50% 100%, #fe7800 0%, #ff9d45 100%);
  ${(p: StyledProps) =>
    p.shieldImage &&
    `
    border-radius: 0;
    box-shadow: none;
    background: unset;
    img {
      padding: 0;
    }
  `};
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    height: 3.25rem;
    width: 3.25rem;
  }
`;
SliderImageContainer.displayName = 'SliderImageContainer';

const SliderImage = styled.img<StyledProps>`
  padding: ${(p: StyledProps) => (p.shieldImage ? '0' : '1.25rem')};
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    height: 3.25rem;
    width: 3.25rem;
    padding: ${(p: StyledProps) => (p.shieldImage ? '0' : '1rem')};
  }
`;
SliderImage.displayName = 'SliderImage';

const SliderText = styled.div`
  padding: 0 0 0 2rem;
  display: flex;
  flex-direction: column;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0 0.25rem 0 1.125rem;
  }
`;
SliderText.displayName = 'SliderText';

const SliderLabel = styled.p`
  font-size: 0.75rem;
  line-height: 1.125rem;
  font-weight: 400;
  color: ${colors.TEXT_PRIMARY};
  sup {
    position: relative;
    top: 0.125rem;
    padding: 0 0 0 0.0625rem;
    font-size: 0.625rem;
  }
`;
SliderLabel.displayName = 'SliderLabel';

const SliderCardTitle = styled.p`
  margin: 0 0 0.4375rem;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.25rem;
  color: ${colors.TEXT_PRIMARY};
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 0 0 0.625rem;
  }
`;
SliderCardTitle.displayName = 'SliderCardTitle';

const ArrowButton = styled.button`
  z-index: 1;
  position: absolute;
  top: 35.5%;
  left: -0.625rem;
  height: 2rem;
  width: 2rem;
  border-radius: 50%;
  padding: 0.75rem;
  box-shadow: 0.0625rem 0.25rem 0.625rem 0 #0000000d;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 0.5rem;
  background-color: #ffffff;
  outline: none;
  cursor: pointer;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    left: -0.875rem;
    height: 2.375rem;
    width: 2.375rem;
    box-shadow: 0.0625rem 0.25rem 0.625rem 0 #0000000f;
  }
`;
ArrowButton.displayName = 'ArrowButton';

const NextArrowButton = styled(ArrowButton)`
  left: unset;
  right: -0.625rem;
`;
NextArrowButton.displayName = 'NextArrowButton';

const ArrowImage = styled(ChevronArrowSVG)`
  height: 0.625rem;
  width: 0.3125rem;
`;
ArrowImage.displayName = 'ArrowImage';

const NextArrowImage = styled(ChevronArrowSVG)`
  height: 0.625rem;
  width: 0.3125rem;
  transform: rotate(180deg);
`;
NextArrowImage.displayName = 'NextArrowImage';

const CarouselCard = (props: CarouselCardProps) => {
  const { id, image, label, shieldImage } = props;

  return (
    <SliderCard id={`slider-card-${id}`}>
      <SliderImageContainer
        id={`slider-image-container-${id}`}
        shieldImage={shieldImage}
      >
        <SliderImage
          id={`slider-card-image-${id}`}
          src={image}
          alt={id}
          shieldImage={shieldImage}
        />
      </SliderImageContainer>
      <SliderText id={`slider-card-text-${id}`}>
        <SliderCardTitle>Did you know?</SliderCardTitle>
        <SliderLabel
          id={`slider-card-label-${id}`}
          dangerouslySetInnerHTML={{ __html: label }}
        />
      </SliderText>
    </SliderCard>
  );
};

const PrevArrow = (props: any) => {
  const { onClick } = props;
  return (
    <ArrowButton id="previous-carousel-card-button" onClick={onClick}>
      <ArrowImage aria-label="Button to show previous carousel card" />
    </ArrowButton>
  );
};

const NextArrow = (props: any) => {
  const { onClick } = props;
  return (
    <NextArrowButton id="next-carousel-card-button" onClick={onClick}>
      <NextArrowImage
        aria-label="Button to show next carousel card"
      />
    </NextArrowButton>
  );
};

export const Carousel: FC<CarouselProps> = ({ cards }) => {
  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
  };

  return (
    <Container id="carousel">
      <SliderWindow id="carousel-window">
        <Slider {...sliderSettings}>
          {cards.map((card) => (
            <CarouselCard
              key={card.id}
              id={card.id}
              image={card.image}
              label={card.label}
              shieldImage={card.shieldImage ?? false}
            />
          ))}
        </Slider>
      </SliderWindow>
    </Container>
  );
};
